import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, BookMarked, BookOpen, LifeBuoy, Loader2, Settings } from 'lucide-react';
import KpiPills from '../v3/KpiPills';
import type { QueueQuickFilter, S3Kpi } from '../v3/kpiModel';
import type { OperatorSupportApi } from '../../../services/supportApi';
// La pelle della barra e degli stati (`support-v4/rep.css`) arriva dal foglio
// radice `support-v4.css`, importato una volta da `lookContext.tsx`.

export interface RepCrumb {
  label: string;
  to?: string;
}

/** Strumento della barra: link (`to`) oppure bottone (`onClick`, es. la modale del video guida). */
export interface RepTool {
  to?: string;
  onClick?: () => void;
  label: string;
  icon: React.ReactNode;
}

interface RepTitleBarProps {
  /** Percorso sopra il titolo (design L39, L857: «Area rappresentativa › Supporto»). */
  crumbs: RepCrumb[];
  title: string;
  /** Pillole KPI (kpiModel, TOTALI compreso: N22). Vuoto = nessuna striscia. */
  kpis: S3Kpi[];
  /** `quick` (Task 46): «BOZZE» filtra con `pendingOnly`. */
  current: { status: string; unread: boolean; quick?: QueueQuickFilter };
  onPickKpi: (kpi: S3Kpi) => void;
  /**
   * Strumenti rotondi (design L60-L65, L834-L839): Guida, Base di conoscenza,
   * Notifiche email. Il toggle tema del prototipo NO: la shell associativa ha
   * gia il suo (N24). Q7: questi link stanno SOLO qui.
   */
  tools: RepTool[];
}

/**
 * Barra sticky v4 dell'area rappresentante (design L35-L71 con `ruolo=rappresentante`,
 * max 1180 px L828). Non riusa `v3/StickyTitleBar`: quello monta sempre il toggle
 * tema admin (`V2ThemeToggle`, doppione nella shell associativa) e il contenitore
 * `.a2-wrap` da 1780 px, pensati per `V2Shell`.
 * La radice porta `sup3` (token `--a2-*`) e `sup4` (pelle v4): funziona anche
 * fuori dalla radice della coda o del ticket.
 */
const RepTitleBar: React.FC<RepTitleBarProps> = ({ crumbs, title, kpis, current, onPickKpi, tools }) => (
  <div className="sup3 sup4 s4-repbar">
    <div className="s4-repbar__inner">
      <span className="s4-repbar__icon" aria-hidden="true"><LifeBuoy size={17} /></span>
      <div className="s4-repbar__titles">
        <nav className="s4-repbar__crumb" aria-label="Percorso">
          {crumbs.map((c, i) => (
            <React.Fragment key={`${c.label}-${i}`}>
              {i > 0 && <span aria-hidden="true"> › </span>}
              {c.to ? <Link to={c.to}>{c.label}</Link> : <span>{c.label}</span>}
            </React.Fragment>
          ))}
        </nav>
        <h1 className="s4-repbar__h1">{title}</h1>
      </div>

      {kpis.length > 0 && (
        <KpiPills skin="s4" className="s4-repbar__kpis" kpis={kpis} current={current} onPickKpi={onPickKpi} />
      )}

      {tools.length > 0 && (
        <div className="s4-repbar__tools">
          {tools.map((t) => (t.to ? (
            <Link key={t.to} to={t.to} className="s4-repbar__tool" aria-label={t.label} title={t.label}>
              {t.icon}
            </Link>
          ) : (
            <button key={t.label} type="button" onClick={t.onClick} className="s4-repbar__tool" aria-label={t.label} title={t.label}>
              {t.icon}
            </button>
          )))}
        </div>
      )}
    </div>
  </div>
);

export default RepTitleBar;

/** Percorso della coda (design L857): «Area rappresentativa › Supporto». */
export const REP_CRUMBS: RepCrumb[] = [{ label: 'Area rappresentativa', to: '/area-associativa' }, { label: 'Supporto' }];

/** Pagina «Impostazioni canali» del responsabile di canale (Task 46). */
export const REP_CHANNELS_PATH = '/area-associativa/supporto/canali';
export const REP_CHANNELS_LABEL = 'Impostazioni canali';

/**
 * Strumenti della barra (Q7: Guida, Base di conoscenza, Notifiche email stanno SOLO qui).
 * `canManageChannels` (Task 46): ingranaggio verso le impostazioni dei canali.
 */
export const repTools = (canKnowledge: boolean, canManageChannels = false): RepTool[] => [
  { to: '/area-associativa/guida-ticket', label: 'Guida all\'uso', icon: <BookOpen size={14} aria-hidden="true" /> },
  ...(canKnowledge
    ? [{ to: '/area-associativa/supporto/conoscenza', label: 'Base di conoscenza', icon: <BookMarked size={14} aria-hidden="true" /> }]
    : []),
  { to: '/area-associativa/supporto/notifiche', label: 'Notifiche email', icon: <Bell size={14} aria-hidden="true" /> },
  ...(canManageChannels
    ? [{ to: REP_CHANNELS_PATH, label: REP_CHANNELS_LABEL, icon: <Settings size={14} aria-hidden="true" /> }]
    : []),
];

/**
 * Il link alle impostazioni dei canali compare solo se il rappresentante ha il
 * permesso `associativa.supporto.manage_channel` E `channels/manage` restituisce
 * almeno un canale (e componente di qualcosa da gestire). Errori = niente link.
 */
export const useCanManageChannels = (api: Pick<OperatorSupportApi, 'getManagedChannels'>, hasPermission: boolean): boolean => {
  const [can, setCan] = useState(false);
  const getManaged = api.getManagedChannels;
  useEffect(() => {
    if (!hasPermission || !getManaged) {
      setCan(false);
      return;
    }
    let alive = true;
    Promise.resolve()
      .then(() => getManaged())
      .then((list) => { if (alive) setCan(Array.isArray(list) && list.length > 0); })
      .catch(() => { if (alive) setCan(false); });
    return () => { alive = false; };
  }, [hasPermission, getManaged]);
  return can;
};

/** Filtro chiesto da una pillola KPI della barra del dettaglio: viaggia in `location.state`. */
export interface RepKpiNavState {
  kpiFilter?: { status: string; unread: boolean; quick?: QueueQuickFilter };
}

/**
 * Avviso bloccante con la pelle v4 («Funzione non abilitata…», «Nessun canale di
 * supporto assegnato»): stessi testi della v3, cambia solo la veste (RA-12).
 */
export const RepNotice: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="sup3 sup4 s4-repnote">
    <p className="s4-repnote__t">{title}</p>
    <p className="s4-repnote__d">{children}</p>
  </div>
);

/** Caricamento / ticket non trovato con la pelle v4 (RA-12): testi invariati. */
export const RepState: React.FC<{ loading?: boolean; children: React.ReactNode }> = ({ loading, children }) => (
  <div className="sup3 sup4 s4-repstate">
    {loading && <Loader2 className="s4-repstate__spin" size={16} aria-hidden="true" />}
    {children}
  </div>
);
